import type { Capture, Descriptor, Use } from './types';

type Entry = NonNullable<Descriptor['products']>[number];

/**
 * The addresses of other products' tasks a snapshot leads to (cross-product tasks, D54), from the family
 * bar's descriptor: publishing it on CDN, opening its project in Packages, a release that uses it, and
 * enabling Packages for a project in Projects. Each carries identifiers the receiving product verifies and
 * never a credential; the way back is built by the receiver. Undefined when the descriptor names no such
 * product (or cannot be read), so nothing leads nowhere.
 */
export class Links {
	#entries: Entry[];
	#packages?: string;

	/** @param fallback The Packages interface this application was configured with, for a descriptor without its entry */
	constructor(descriptor: Descriptor | { unavailable: true } | null | undefined, { packages }: { packages?: string } = {}) {
		this.#entries = descriptor && !('unavailable' in descriptor) ? (descriptor.products ?? []) : [];
		this.#packages = packages || undefined;
	}

	entry(product: string): Entry | undefined {
		return this.#entries.find(entry => entry.product === product);
	}

	#origin(product: string): string | undefined {
		const url = this.entry(product)?.url;
		try {
			return url ? new URL(url).origin : undefined;
		} catch {
			return undefined;
		}
	}

	/** CDN's publication task for a completed snapshot */
	publish(capture: Capture): string | undefined {
		const cdn = this.#origin('cdn');
		if (!cdn || capture.state !== 'active') return undefined;
		const query = new URLSearchParams({ capture: capture.id, project: capture.project, from: 'snapshots' });
		return `${cdn}/o/${encodeURIComponent(capture.organization)}/publish?${query}`;
	}

	/** Packages for the snapshot's project, where snapshots are made */
	packages(capture: { organization: string; project: string }): string | undefined {
		const entry = this.entry('packages');
		if (entry?.url) return entry.url;
		if (!this.#packages) return undefined;
		return `${this.#packages}/?${new URLSearchParams({ organization: capture.organization, project: capture.project })}`;
	}

	/** Where something that uses a snapshot is, on its holder's interface; its product otherwise */
	use(use: Use): string | undefined {
		const origin = this.#origin(use.holder);
		if (origin && use.path) return `${origin}${use.path}`;
		return this.entry(use.holder)?.url ?? undefined;
	}

	/**
	 * Whether Packages is enabled for the project in view, as the descriptor says: `enabled`, `off` with whether
	 * this person can enable it, or `unknown`
	 */
	get enablement(): { state: 'enabled' | 'off' | 'unknown'; enable: boolean; where?: string } {
		const entry = this.entry('packages');
		if (!entry) return { state: 'unknown', enable: false };
		if (entry.reason === 'NOT_ENABLED') return { state: 'off', enable: entry.enable === true, where: this.entry('projects')?.url ?? undefined };
		return { state: entry.available ? 'enabled' : 'unknown', enable: false };
	}
}
